/* eslint-disable @typescript-eslint/naming-convention */
import moment from 'moment';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import formatDate from '../formatDate';
import formatPhoneNumber from '../formatPhoneNumber';
import pushClevertapEvent from '../pushClevertapEvent';

export function setUserProfile({
  profile,
  businessTypes,
  businessCategories,
  loyalty,
  paymentMethods,
}: {
  profile: any;
  businessTypes: any;
  businessCategories: any;
  loyalty?: any;
  paymentMethods?: any;
}) {
  const businessTypeName = get(
    find(businessTypes, (t) => t.id === profile.business.business_type_id),
    'name',
    ''
  );
  const businessCategoryName = get(
    find(
      businessCategories,
      (c) => c.id === profile.business.business_category_id
    ),
    'name',
    ''
  );

  const featureSubscription = profile.feature_subscription;
  const crmProStatus =
    featureSubscription.find((t) => t.feature_name === 'Loyalty Pro')
      ?.is_subscribing ?? false;
  const ingredientInventoryStatus =
    featureSubscription.find((t) => t.feature_name === 'Ingredient Inventory')
      ?.is_subscribing ?? false;
  const tableManagementStatus =
    featureSubscription.find((t) => t.feature_name === 'Table Management')
      ?.is_subscribing ?? false;

  let accountLevel: string | null = null;

  if (profile.is_owner) {
    accountLevel = 'Owner';
  } else if (profile.is_manager) {
    accountLevel = 'Administrator';
  } else {
    accountLevel = 'Employee';
  }

  let paidStatus;
  const {
    is_mokapos_user: isMokaPosUser,
    is_trial: isTrial,
    has_expired: hasExpired,
  } = profile.business;
  if (isMokaPosUser) {
    if (isTrial) {
      paidStatus = 'Trial';
    } else if (hasExpired) {
      paidStatus = 'Churn';
    } else {
      paidStatus = 'Paid';
    }
  } else {
    paidStatus = 'Free';
  }

  const subscriptionData = {};
  if (profile.is_owner) {
    subscriptionData['Active Subscription'] = profile.already_subscribed;
    if (profile.business.is_trial) {
      subscriptionData['Paid Outlet Count'] = 0;
    } else {
      subscriptionData['Paid Outlet Count'] = profile.business.outlet_slot;
    }
  }

  const loyaltyData = {};
  if (loyalty) {
    let loyaltyCreated = 'No';
    let loyaltyStatus = 'Inactive';
    const loyaltyCreatedProperties = 'loyalty_program_created';
    const loyaltyStatusProperties = 'loyalty_program_status';
    if (!isEmpty(loyalty)) {
      const [data] = loyalty;
      loyaltyCreated = 'Yes';
      if (data.status === 'ACTIVE') {
        loyaltyStatus = 'Active';
      }
    }

    loyaltyData[loyaltyCreatedProperties] = loyaltyCreated;
    loyaltyData[loyaltyStatusProperties] = loyaltyStatus;
  }

  const paymentData = {};
  if (paymentMethods) {
    paymentMethods.forEach((payment) => {
      const properties = `${payment.code}_status`;
      if (payment.outlet_statuses) {
        const outlets = payment.outlet_statuses;
        let foundApproved = false;
        for (const outletId in outlets) {
          if (outlets[outletId] === 'APPROVED') {
            foundApproved = true;
          }
        }
        paymentData[properties] = foundApproved ? 'APPROVED' : 'PENDING';
      } else {
        paymentData[properties] = 'NOT_AVAILABLE';
      }
    });
  }

  const userProfile = {
    Name: `${profile.first_name} ${profile.last_name}`,
    Identity: profile.id,
    Email: profile.email,
    'First Name': profile.first_name,
    'Last Name': profile.last_name,
    Created: formatDate(profile.created_at),
    'Last Login': formatDate(moment()),
    Phone: formatPhoneNumber(profile.phone),
    'Business Name': profile.business.name,
    'Business Type ID': profile.business.business_type_id,
    'Business Category ID': profile.business.business_category_id,
    'Business Address': profile.business.address,
    'Business City': profile.business.city,
    'Business State': profile.business.province,
    'Business ZIP': profile.business.zip,

    // This field is added specifically to allow user targetting with email.
    // Somehow Clevertap doesn't support using the `Email` field above.
    'User Email': profile.email,

    'Phone Number': formatPhoneNumber(profile.phone),
    Expired: !profile.business.expired_at
      ? ''
      : formatDate(profile.business.expired_at),
    'Days to Expire': !profile.business.expired_at
      ? 0
      : moment(profile.business.expired_at).diff(moment(), 'days'),
    'Business Type Name': businessTypeName,
    'Business Category Name': businessCategoryName,
    'Subscription Plan':
      // eslint-disable-next-line no-nested-ternary
      profile.business.current_plan === 12
        ? 'Annual'
        : profile.business.current_plan === 1
        ? 'Monthly'
        : '',
    'Account Level': accountLevel,
    'Paid Status': paidStatus,
    crm_pro_status: crmProStatus ? 'Subscribe' : 'Unsubscribe',
    ingredient_inventory_status: ingredientInventoryStatus
      ? 'Subscribe'
      : 'Unsubscribe',
    table_management_status: tableManagementStatus
      ? 'Subscribe'
      : 'Unsubscribe',
    ...subscriptionData,
    ...paymentData,
    ...loyaltyData,
  };

  clevertap?.onUserLogin.push({ Site: userProfile });
}

export function login({ profile, role }) {
  const planTypes = {
    1: 'Monthly',
    12: 'Annual',
  };
  const planType = planTypes[profile.business.current_plan] || '';
  pushClevertapEvent('Login', {
    'User ID': profile.id,
    'Active Subscription': profile.already_subscribed,
    'Is Business Owner': profile.is_owner,
    'Subscription Plan': planType,
    'Expiration Date': formatDate(profile.business.expired_at),
    Email: profile.email,
    Role: role ? role.name : '',
  });
}
